// Cookie-consent state for the marketing site.
//
// A single binary choice gates ALL non-essential trackers (PostHog analytics +
// ad pixels). Absence of a stored value means "undecided": the banner shows and
// nothing loads. Reject is as easy as accept (GDPR/ePrivacy requirement).
//
// The choice is persisted as a structured record — choice + timestamp + versions
// — so we can (a) demonstrate WHAT was consented to and WHEN, (b) re-prompt when
// consent goes stale (>12 months), and (c) force fresh consent when the vendor
// set changes, by bumping CONSENT_VERSION. Legacy acceptance without evidence requires a fresh choice; legacy refusal is honored.
//
// Pure module — no React, no DOM beyond `localStorage`. Safe to import from SSR
// (every browser access is guarded / try-caught).

import { PRIVACY_POLICY_VERSION } from '@docio/shared';

export type ConsentChoice = 'accepted' | 'rejected';

export interface ConsentRecord {
  /** Local decision scope, never an analytics identity or vendor property. */
  decisionId?: string;
  /** The visitor's binary choice. */
  choice: ConsentChoice;
  /** ms epoch of when the choice was made — the audit timestamp. */
  timestamp: number;
  /** Bumped when the vendor set changes; a mismatch forces a re-prompt. */
  consentVersion: number;
  /** Privacy-policy revision the consent was captured under (evidentiary tag). */
  policyVersion: string;
}

const STORAGE_KEY = 'docio_cookie_consent';

/** Bump when the set of trackers changes — invalidates every prior consent. */
export const CONSENT_VERSION = 1;

/** Privacy-policy revision shipped alongside this consent UI (evidentiary tag). */
export const POLICY_VERSION = PRIVACY_POLICY_VERSION;

/** Consent goes stale after ~12 months and the banner re-appears (CNIL ≤13 mo). */
const CONSENT_MAX_AGE_MS = 12 * 30 * 24 * 60 * 60 * 1000;

type Listener = (choice: ConsentChoice | null) => void;
const listeners = new Set<Listener>();
let deniedInMemory = false;

function readRaw(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage can throw in privacy mode / when disabled — treat as undecided.
    return null;
  }
}

function writeRecord(record: ConsentRecord): boolean {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    return true;
  } catch {
    // Fail closed: a failed withdrawal retains an in-memory denial.
    return false;
  }
}

/** Returns recorded evidence without inventing dates or notice versions for legacy choices. */
export function getConsentRecord(): ConsentRecord | null {
  const raw = readRaw();
  if (!raw) return null;

  // Undocumented choices cannot establish fresh acceptance evidence.
  if (raw === 'accepted' || raw === 'rejected') return null;

  try {
    const parsed = JSON.parse(raw) as Partial<ConsentRecord>;
    if (parsed?.choice !== 'accepted' && parsed?.choice !== 'rejected') return null;
    if (typeof parsed.timestamp !== 'number') return null;
    return {
      choice: parsed.choice,
      timestamp: parsed.timestamp,
      ...(typeof parsed.decisionId === 'string' ? { decisionId: parsed.decisionId } : {}),
      // Absent versions (from a partially-written record) count as outdated, so
      // getConsent() re-prompts rather than honoring an ambiguous record.
      consentVersion: typeof parsed.consentVersion === 'number' ? parsed.consentVersion : 0,
      policyVersion: typeof parsed.policyVersion === 'string' ? parsed.policyVersion : '',
    };
  } catch {
    return null;
  }
}

/**
 * Returns the effective stored choice, or `null` when the visitor is undecided.
 * `null` also covers a prior acceptance that has gone stale (older than
 * CONSENT_MAX_AGE_MS) or was captured under an outdated notice/vendor version. In
 * every `null` case the banner re-appears and nothing loads until a fresh choice.
 */
export function getConsent(): ConsentChoice | null {
  if (deniedInMemory) return 'rejected';
  if (readRaw() === 'rejected') return 'rejected';
  const record = getConsentRecord();
  if (!record) return null;
  if (record.choice === 'rejected') return 'rejected';
  if (!Number.isFinite(record.timestamp) || record.timestamp <= 0 || record.timestamp > Date.now()) return null;
  if (record.policyVersion !== POLICY_VERSION) return null;
  if (record.consentVersion !== CONSENT_VERSION) return null;
  if (Date.now() - record.timestamp > CONSENT_MAX_AGE_MS) return null;
  return record.choice;
}

/** Persists the choice (with a fresh timestamp + versions) and notifies subscribers. */
export function setConsent(choice: ConsentChoice): void {
  const stored = writeRecord({
    decisionId: globalThis.crypto.randomUUID(),
    choice,
    timestamp: Date.now(),
    consentVersion: CONSENT_VERSION,
    policyVersion: POLICY_VERSION,
  });
  if (choice === 'rejected') deniedInMemory = !stored;
  else if (stored) deniedInMemory = false;
  listeners.forEach((cb) => cb(getConsent()));
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function subscribeConsent(cb: Listener): () => void {
  listeners.add(cb);
  const sync = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) cb(getConsent());
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', sync);
  return () => {
    listeners.delete(cb);
    if (typeof window !== 'undefined') window.removeEventListener('storage', sync);
  };
}
